import moment from "moment";
import { ActionContext, ActionTree } from "vuex";
import { Casier, ICasierCDS } from "../../models/Casier";
import {
  ILocationCDS,
  INouvelleLocation,
  Location,
} from "../../models/Location";
import { INiveauCDS, Niveau } from "../../models/Niveau";
import { IPavillonCDS, Pavillon } from "../../models/Pavillon";
import { IPeriodeCDS, Periode } from "../../models/Periode";
import { IPilotageCDS, Pilotage } from "../../models/Pilotage";
import { ITypeClienteleCDS, TypeClientele } from "../../models/TypeClientele";
import {
  IVariableEnvironnementDefinitionCDS,
  VariableEnvironnementDefinition,
} from "../../models/VariableEnvironnementDefinition";
import {
  IVariableEnvironnementValueCDS,
  VariableEnvironnementValue,
} from "../../models/VariableEnvironnementValue";
import { mutations } from "../cds/mutations";
import { ICDSState, IRacineState } from "../models";
import { vm } from "../types";
import { mutations as mutationsUtilisateur } from "../utilisateur/index";

export interface IActions {
  recupererLocations: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
      idUtilisateur: string;
    }
  ) => void;
  recupererTypesClientele: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
    }
  ) => void;
  recupererPeriodes: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      typePeriodeId: string;
      vm: vm;
    }
  ) => void;
  recupererPavillonsAvecCasiersDispos: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      idUtilisateur: string;
      vm: vm;
    }
  ) => void;
  recupererNiveauxAvecCasiersDispos: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      pavillonId: string;
      idUtilisateur: string;
      vm: vm;
    }
  ) => void;
  recupererCasiersDisposEtBonEtatParNiveau: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      niveauId: string;
      idUtilisateur: string;
      vm: vm;
    }
  ) => void;
  recupererDisponibiliteCasier: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
      idCasier: string;
      idUtilisateur: string;
    }
  ) => Promise<boolean>;
  recupererDefinitionsVariablesEnv: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
    }
  ) => void;
  recupererValeursVariablesEnv: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
    }
  ) => void;
  recupererParametresPilotage: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
    }
  ) => void;
  actualiserVerrouUtilisateur: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
      idCasier: string;
      idUtilisateur: string;
      delaiVerrou: number;
    }
  ) => void;
  retirerVerrouUtilisateur: (
    context: ActionContext<ICDSState, IRacineState>,
    options: {
      vm: vm;
      idCasier: string;
    }
  ) => void;
  creerLocation: (
    context: ActionContext<ICDSState, IRacineState>,
    options: { vm: vm; location: INouvelleLocation }
  ) => void;
}

export const actions: IActions & ActionTree<ICDSState, IRacineState> = {
  async recupererLocations(context, { vm, idUtilisateur }) {
    const resp = await interrogerCDS<ILocationCDS>(
      vm,
      `si_locationcasiers` +
        `?$expand=si_Typedeclientele($select=si_typedeclienteleid),` +
        `si_CasierSI($select=si_casiersiid, si_nocasier, si_ImmeubleSI),` +
        `si_NiveauSI,` +
        `si_ImmeubleSI,` +
        `si_Periodedelocation($select=si_periodedelocationid, si_datedebutlocation, si_datefinlocation)` +
        `&$filter=si_identifiant_locateur eq '${idUtilisateur}'` +
        `&$orderby=createdon desc`
    );
    const locations = resp.data.value.map((location) => new Location(location));
    context.commit(mutations.recupererLocationsSucces.name, locations);
  },

  async recupererTypesClientele(context, { vm }) {
    const resp = await interrogerCDS<ITypeClienteleCDS>(
      vm,
      "si_typedeclienteles?$orderby=si_ordretri asc"
    );

    const typesClientele = resp.data.value.map(
      (typeClientele) => new TypeClientele(typeClientele)
    );
    context.commit(
      mutations.recupererTypesClienteleSucces.name,
      typesClientele
    );
  },

  async recupererPeriodes(context, { typePeriodeId, vm }) {
    const resp = await interrogerCDS<IPeriodeCDS>(
      vm,
      `si_periodedelocations` +
        `?$filter=si_periodeactive eq true` +
        (typePeriodeId !== null
          ? ` and si_Typedeperiodedelocation/si_typedeperiodedelocationid eq ${typePeriodeId}`
          : "") +
        `&$orderby=si_ordretri asc`
    );

    const periodes = resp.data.value.map((periode) => new Periode(periode));
    context.commit(mutations.recupererPeriodesSucces.name, periodes);
  },

  async recupererPavillonsAvecCasiersDispos(context, { idUtilisateur, vm }) {
    const resp = await interrogerCDS<IPavillonCDS>(
      vm,
      `si_immeublesis` +
        `?$expand=si_CasierSI_si_ImmeubleSI_si_ImmeubleSI($filter=si_disponibiliteducasier eq ${context.rootState.applicatif.variableEnvCleOptionCasierDisponible}` +
        ` and (si_verrouutilisateur eq null or si_verrouutilisateur eq ${idUtilisateur})  ` +
        ` and si_EtatcasierSI/si_locationautorisee eq true)` +
        `&$filter=si_NiveauSI_si_ImmeubleSI_si_ImmeubleSI/any(o:o/si_niveau ne null)` +
        `&$orderby=si_nomimmeuble asc`
    );

    const pavillons = resp.data.value.map((pavillon) => new Pavillon(pavillon));
    context.commit(
      mutations.recupererPavillonsAvecCasiersDisposSucces.name,
      pavillons
    );
  },
  async recupererNiveauxAvecCasiersDispos(
    context,
    { pavillonId, idUtilisateur, vm }
  ) {
    const resp = await interrogerCDS<INiveauCDS>(
      vm,
      `si_niveausis` +
        `?$expand=si_Typedeclientele($select=si_typedeclienteleid),` +
        `si_CasierSI_si_NiveauSI_si_NiveauSI($filter=si_disponibiliteducasier eq ${context.rootState.applicatif.variableEnvCleOptionCasierDisponible}` +
        ` and (si_verrouutilisateur eq null or si_verrouutilisateur eq ${idUtilisateur})` +
        ` and si_EtatcasierSI/si_locationautorisee eq true)` +
        `&$select=si_niveau,si_message,si_Typedeclientele,si_nomplan` +
        `&$filter=si_ImmeubleSI/si_immeublesiid eq ${pavillonId}` +
        `&$orderby=si_niveau asc`
    );

    const niveaux = resp.data.value.map((niveau) => new Niveau(niveau));
    context.commit(
      mutations.recupererNiveauxAvecCasiersDisposSucces.name,
      niveaux
    );
  },

  async recupererCasiersDisposEtBonEtatParNiveau(
    context,
    { niveauId, idUtilisateur, vm }
  ) {
    const resp = await interrogerCDS<ICasierCDS>(
      vm,
      `si_casiersis?$filter=si_NiveauSI/si_niveausiid eq ${niveauId}` +
        ` and si_EtatcasierSI/si_locationautorisee eq true` +
        ` and si_disponibiliteducasier eq ${context.rootState.applicatif.variableEnvCleOptionCasierDisponible}` +
        ` and (si_verrouutilisateur eq null or si_verrouutilisateur eq ${idUtilisateur})  ` +
        `&$expand=si_ImmeubleSI($select=si_immeublesiid, si_nomimmeuble),` +
        ` si_NiveauSI($select=si_niveausiid, si_niveau)` +
        `&$orderby=si_nocasier asc`
    );

    const casiers = resp.data.value.map((casier) => new Casier(casier));
    context.commit(
      mutations.recupererCasiersDisposEtBonEtatParNiveauSucces.name,
      casiers
    );
  },
  async recupererDisponibiliteCasier(
    context,
    { vm, idCasier, idUtilisateur }
  ): Promise<boolean> {
    const resp = await interrogerCDS<ICasierCDS>(
      vm,
      `si_casiersis?$filter=si_casiersiid eq '${idCasier}'` +
        ` and si_EtatcasierSI/si_locationautorisee eq true` +
        ` and si_disponibiliteducasier eq ${context.rootState.applicatif.variableEnvCleOptionCasierDisponible}` +
        ` and (si_verrouutilisateur eq null or si_verrouutilisateur eq ${idUtilisateur})`
    );
    return resp.data.value.length > 0;
  },

  async recupererDefinitionsVariablesEnv(context, { vm }) {
    const resp = await interrogerCDS<IVariableEnvironnementDefinitionCDS>(
      vm,
      "environmentvariabledefinitions"
    );

    const definitionsVariablesEnv = resp.data.value.map(
      (variableEnvironnementDefinition) =>
        new VariableEnvironnementDefinition(variableEnvironnementDefinition)
    );
    context.commit(
      mutations.recupererDefinitionsVariablesEnvSucces.name,
      definitionsVariablesEnv
    );
  },
  async recupererValeursVariablesEnv(context, { vm }) {
    const resp = await interrogerCDS<IVariableEnvironnementValueCDS>(
      vm,
      "environmentvariablevalues"
    );

    const valeursVariablesEnv = resp.data.value.map(
      (variableEnvironnementValue) =>
        new VariableEnvironnementValue(variableEnvironnementValue)
    );
    context.commit(
      mutations.recupererValeursVariablesEnvSucces.name,
      valeursVariablesEnv
    );
  },

  async recupererParametresPilotage(context, { vm }) {
    const resp = await interrogerCDS<IPilotageCDS>(vm, "si_pilotages");

    const parametresPilotage = resp.data.value.map(
      (pilotage) => new Pilotage(pilotage)
    );
    context.commit(
      mutations.recupererParametresPilotageSucces.name,
      parametresPilotage
    );
  },

  async actualiserVerrouUtilisateur(
    context,
    { vm, idCasier, idUtilisateur, delaiVerrou }
  ) {
    const dateFinVerrou: string = moment
      .utc()
      .add(delaiVerrou, "minutes")
      .format("YYYY-MM-DD HH:mm:ss");
    await mettreAJourEntiteCDS<ICasierCDS>(vm, `si_casiersis(${idCasier})`, {
      si_verrouutilisateur: idUtilisateur,
      si_datefinverrou: dateFinVerrou,
    });
  },

  async retirerVerrouUtilisateur(context, { vm, idCasier }) {
    await mettreAJourEntiteCDS<ICasierCDS>(vm, `si_casiersis(${idCasier})`, {
      si_verrouutilisateur: null,
      si_datefinverrou: null,
    });
  },

  async creerLocation(context, { vm, location }) {
    const resp = await creerEntiteCDS<ILocationCDS>(
      vm,
      `si_locationcasiers` +
        `?$expand=si_Typedeclientele,` +
        `si_CasierSI($select=si_nocasier),` +
        `si_NiveauSI,` +
        `si_ImmeubleSI,` +
        `si_Periodedelocation($select=si_nomdelaperiodedelocation)`,
      location
    );

    context.commit(
      mutationsUtilisateur.creerNouvelleLocationSucces.name,
      new Location(resp.data)
    );
  },
};

function interrogerCDS<T>(vm: vm, chemin: string) {
  const scopeProxy = process.env["VUE_APP_PROXY_BASE_SCOPE"] || "";
  const urlProxy = process.env["VUE_APP_PROXY_BASE_URL"] || "";

  return vm.$msal.query<{
    value: Array<T>;
  }>(
    `${urlProxy}/api/data/v9.1/${chemin}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
      },
      data: {}, // Permet de conserver l'en-tête "Content-Type" (bug contenu dans la lib axios - https://github.com/axios/axios)
      method: "GET",
      responseType: "json",
    },
    {
      // Query tentera de récupérer un token avec les accès requis pour envoyer des requêtes vers le CDS
      scopes: [`${scopeProxy}/Access`],
    }
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mettreAJourEntiteCDS<T>(vm: vm, chemin: string, corps: any) {
  const scopeProxy = process.env["VUE_APP_PROXY_BASE_SCOPE"] || "";
  const urlProxy = process.env["VUE_APP_PROXY_BASE_URL"] || "";

  return vm.$msal.query<{
    value: Array<T>;
  }>(
    `${urlProxy}/api/data/v9.1/${chemin}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
      },
      data: corps,
      method: "PATCH",
      responseType: "json",
    },
    {
      // Query tentera de récupérer un token avec les accès requis pour envoyer des requêtes vers le CDS
      scopes: [`${scopeProxy}/Access`],
    }
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function creerEntiteCDS<T>(vm: vm, chemin: string, corps: any) {
  const scopeProxy = process.env["VUE_APP_PROXY_BASE_SCOPE"] || "";
  const urlProxy = process.env["VUE_APP_PROXY_BASE_URL"] || "";

  return vm.$msal.query<T>(
    `${urlProxy}/api/data/v9.1/${chemin}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        mode: "cors",
        Prefer: "return=representation",
      },
      data: corps,
      method: "POST",
      responseType: "json",
    },
    {
      // Query tentera de récupérer un token avec les accès requis pour envoyer des requêtes vers le CDS
      scopes: [`${scopeProxy}/Access`],
    }
  );
}
